import { withZod } from '@remix-validated-form/with-zod'
import { Plus as PlusIcon } from 'iconoir-react'
import { ReactElement, useState } from 'react'
import { z } from 'zod'
import Button from '../Button'
import FormCombo from '../form/FormCombo'
import FormInput from '../form/FormInput'
import { SmartForm } from '../form/SmartForm'
import type { ContactType } from 'app/types/entities'
import { AdPlatformMappingType, defaultAdPlatforms } from 'lib/adPlatformDefaults'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const adPlatformMappingValidator = withZod(
  z.object({
    source: z.string().min(1, { message: `Source is required` }),
    medium: z.string().min(1, { message: `Medium is required` }),
    platform: z.string().min(1, { message: `Platform is required` }),
  }),
)

export function ContactAdPlatformMappingForm({
  adPlatformMapping,
  handleSubmit,
}: {
  adPlatformMapping?: AdPlatformMappingType
  handleSubmit: (
    data: Partial<AdPlatformMappingType>,
  ) => Promise<({ error?: string; contact?: never } | { error: never; contact: ContactType }) | undefined>
}): ReactElement {
  const [isLoading, setIsLoading] = useState<boolean | undefined>()
  const [selectedAdPlatform, setSelectedAdPlatform] = useState<OptionType | undefined>(
    adPlatformMapping?.platform
      ? { value: adPlatformMapping.platform, id: adPlatformMapping.id!, name: adPlatformMapping.platform }
      : undefined,
  )
  const adPlatformOptions = defaultAdPlatforms.map((platform) => ({
    id: platform,
    name: platform,
    value: platform,
  }))
  return (
    <SmartForm<Partial<AdPlatformMappingType>>
      id="ad-platform-mapping-form"
      validator={adPlatformMappingValidator}
      className="mb-0 mt-0 flex w-full flex-col gap-y-0"
      defaultValues={adPlatformMapping}
      onSubmit={async (data) => {
        setIsLoading(true)
        const reponse = await handleSubmit(data)
        if (reponse?.error) {
          logError(reponse.error)
          setIsLoading(false)
        }
      }}
      hiddenInputs={[{ name: `id`, value: adPlatformMapping?.id }]}
      hideButtons
      preventSubmit
    >
      <div className="col-span-6 flex flex-col px-6 py-4">
        <div className="flex items-start gap-x-2">
          <FormInput
            labelClassName="text-strong text-body-sm font-medium"
            className="h-10 w-44 !px-2 !py-1"
            name="source"
            type="text"
            label="Source"
            cols={3}
            disabled={true}
          />
          <PlusIcon className="size-5 mt-9" strokeWidth={1.5} />
          <FormInput
            labelClassName="text-strong w-44 text-body-sm font-medium"
            className="h-10 !px-2 !py-1"
            name="medium"
            type="text"
            label="Medium"
            cols={3}
            disabled={true}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-5 mt-9"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.499 8.248h15m-15 7.501h15" />
          </svg>
          <FormCombo
            label="Platform"
            labelClassName="text-strong w-44 text-body-sm font-medium"
            inputClassName="!h-10 !px-2 !py-1 text-body-sm font-medium"
            name="platform"
            placeholder="Select Platform"
            value={selectedAdPlatform}
            options={adPlatformOptions}
            onChange={setSelectedAdPlatform}
            cols={3}
          />
        </div>
      </div>
      <div className="border-t-trn col-span-6 flex w-full items-center justify-end border px-6 py-3">
        <Button mode="primary" size="sm" label="Save" type="submit" className="text-center" loading={isLoading} />
      </div>
    </SmartForm>
  )
}
